import React, { useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Task, Milestone } from '../types';
import { getProjectTasks } from '../services/taskService';
import { getProjectMilestones } from '../services/milestoneService';
import TaskDetailDrawer from '../task/TaskDetailDrawer';
import { useTaskPolling, mergeTaskDeltas } from '../hooks/useTaskPolling';

interface Props { pca: IPublicClientApplication; projectId: string; refreshKey?: number; }

type Mode = 'month' | 'week';

// ISO-week-style start-of-week set to Monday. Returns a new Date at local midnight.
function startOfWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = out.getDay(); // 0 Sun .. 6 Sat
  const delta = dow === 0 ? -6 : 1 - dow;
  out.setDate(out.getDate() + delta);
  return out;
}
function startOfMonthGrid(d: Date): Date {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return startOfWeek(first);
}
function endOfMonthGrid(d: Date): Date {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const sow = startOfWeek(last);
  const end = new Date(sow);
  end.setDate(end.getDate() + 6);
  return end;
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function toKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function parseLocalDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  // Accept ISO date-only or full timestamp. Construct a local-midnight Date.
  const s = iso.slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW_NAMES   = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function CalendarView({ pca, projectId, refreshKey }: Props) {
  const [tasks, setTasks]             = useState<Task[]>([]);
  const [milestones, setMilestones]   = useState<Milestone[]>([]);
  const [loading, setLoading]         = useState(true);
  const [mode, setMode]               = useState<Mode>('month');
  const [cursor, setCursor]           = useState<Date>(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  });
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [t, m] = await Promise.all([
          getProjectTasks(pca, projectId),
          getProjectMilestones(pca, projectId).catch(() => [] as Milestone[]),
        ]);
        if (!cancelled) { setTasks(t); setMilestones(m); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pca, projectId, refreshKey]);

  // Real-time refresh — deferred while the task drawer is open.
  useTaskPolling({
    pca, projectId,
    isPaused: () => loading || selectedTask !== null,
    onTasks: deltas => setTasks(prev => mergeTaskDeltas(prev, deltas)),
  });

  const today = useMemo(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  // Bucket tasks + milestones by local-midnight day key.
  const byDay = useMemo(() => {
    const out: Record<string, { tasks: Task[]; milestones: Milestone[] }> = {};
    tasks.forEach(t => {
      const d = parseLocalDate(t.dagrun_pm_duedate);
      if (!d) return;
      const k = toKey(d);
      if (!out[k]) out[k] = { tasks: [], milestones: [] };
      out[k].tasks.push(t);
    });
    milestones.forEach(m => {
      const d = parseLocalDate(m.dagrun_pm_duedate);
      if (!d) return;
      const k = toKey(d);
      if (!out[k]) out[k] = { tasks: [], milestones: [] };
      out[k].milestones.push(m);
    });
    return out;
  }, [tasks, milestones]);

  const step = (delta: number) => {
    const next = new Date(cursor);
    if (mode === 'month') next.setMonth(next.getMonth() + delta);
    else                  next.setDate(next.getDate() + delta * 7);
    setCursor(next);
  };
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), today.getDate()));

  const title = mode === 'month'
    ? `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`
    : (() => {
        const sow = startOfWeek(cursor);
        const eow = new Date(sow); eow.setDate(sow.getDate() + 6);
        const sameMonth = sow.getMonth() === eow.getMonth();
        if (sameMonth) return `${MONTH_NAMES[sow.getMonth()]} ${sow.getDate()}–${eow.getDate()}, ${sow.getFullYear()}`;
        return `${MONTH_NAMES[sow.getMonth()]} ${sow.getDate()} – ${MONTH_NAMES[eow.getMonth()]} ${eow.getDate()}, ${eow.getFullYear()}`;
      })();

  const segBtn = (m: Mode, label: string) => {
    const active = mode === m;
    return (
      <button
        key={m}
        type="button"
        onClick={() => setMode(m)}
        style={{
          background: active ? 'var(--brand-mist)' : 'transparent',
          color: active ? 'var(--brand)' : 'var(--ink-3)',
          fontWeight: active ? 500 : 400,
          fontFamily: 'var(--font-ui)', fontSize: 12,
          border: 'none', borderRadius: 4,
          padding: '4px 10px', cursor: 'pointer',
        }}
      >{label}</button>
    );
  };

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--app-bg)' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', background: '#fff',
        borderBottom: '1px solid #DDE0E6', flexShrink: 0,
      }}>
        <div style={{ display: 'inline-flex', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 'var(--r-sm)' }}>
          {segBtn('month', 'Month')}
          {segBtn('week', 'Week')}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
          <button type="button" onClick={() => step(-1)} style={navBtn}>‹</button>
          <button type="button" onClick={goToday} style={{ ...navBtn, padding: '4px 12px', fontWeight: 500 }}>Today</button>
          <button type="button" onClick={() => step(1)} style={navBtn}>›</button>
        </div>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 400,
          color: 'var(--ink)', marginLeft: 10,
        }}>{title}</div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
        {mode === 'month'
          ? <MonthGrid cursor={cursor} byDay={byDay} today={today} onTaskClick={setSelectedTask} />
          : <WeekGrid  cursor={cursor} byDay={byDay} today={today} onTaskClick={setSelectedTask} />}
      </div>

      <TaskDetailDrawer task={selectedTask} pca={pca} onClose={() => setSelectedTask(null)} />
    </div>
  );
}

const navBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid #DDE0E6', borderRadius: 4,
  color: 'var(--ink-2)', cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontSize: 13,
  padding: '4px 9px',
  lineHeight: 1,
};

// ── Month grid ────────────────────────────────────────────────────────
interface GridProps {
  cursor: Date;
  byDay: Record<string, { tasks: Task[]; milestones: Milestone[] }>;
  today: Date;
  onTaskClick: (t: Task) => void;
}

function MonthGrid({ cursor, byDay, today, onTaskClick }: GridProps) {
  const start = startOfMonthGrid(cursor);
  const end   = endOfMonthGrid(cursor);
  const weeks: Date[][] = [];
  let cur = new Date(start);
  while (cur <= end) {
    const row: Date[] = [];
    for (let i = 0; i < 7; i++) { row.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    weeks.push(row);
  }
  return (
    <div style={{ background: '#fff', border: '1px solid #DDE0E6', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,17,23,0.06)' }}>
      {/* Header row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'var(--surface-2)', borderBottom: '1px solid #DDE0E6' }}>
        {DOW_NAMES.map(n => (
          <div key={n} style={{
            padding: '8px 10px',
            fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600,
            color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{n}</div>
        ))}
      </div>
      {weeks.map((w, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: wi < weeks.length - 1 ? '1px solid #EAECF0' : 'none' }}>
          {w.map((d, di) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = sameDay(d, today);
            const bucket = byDay[toKey(d)];
            return (
              <div key={di} style={{
                minHeight: 100,
                padding: 6,
                borderRight: di < 6 ? '1px solid #EAECF0' : 'none',
                background: isToday ? '#FEF9E7' : '#fff',
                opacity: inMonth ? 1 : 0.45,
                display: 'flex', flexDirection: 'column', gap: 2,
              }}>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: isToday ? 'var(--brand)' : 'var(--ink-3)',
                  fontWeight: isToday ? 600 : 400,
                  marginBottom: 2,
                }}>{d.getDate()}</div>
                {bucket?.milestones.map(m => <MilestoneChip key={m.dagrun_pm_milestoneid} milestone={m} />)}
                {bucket?.tasks.map(t => <TaskChip key={t.dagrun_pm_taskid} task={t} onClick={() => onTaskClick(t)} />)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Week grid ─────────────────────────────────────────────────────────
function WeekGrid({ cursor, byDay, today, onTaskClick }: GridProps) {
  const sow = startOfWeek(cursor);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) { const d = new Date(sow); d.setDate(sow.getDate() + i); days.push(d); }
  return (
    <div style={{ background: '#fff', border: '1px solid #DDE0E6', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,17,23,0.06)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'var(--surface-2)', borderBottom: '1px solid #DDE0E6' }}>
        {days.map((d, i) => {
          const isToday = sameDay(d, today);
          return (
            <div key={i} style={{
              padding: '10px 12px',
              borderRight: i < 6 ? '1px solid #DDE0E6' : 'none',
              background: isToday ? '#FEF9E7' : undefined,
            }}>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{DOW_NAMES[i]}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 500, color: isToday ? 'var(--brand)' : 'var(--ink)', marginTop: 2 }}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', minHeight: 300 }}>
        {days.map((d, i) => {
          const bucket = byDay[toKey(d)];
          return (
            <div key={i} style={{
              padding: 8,
              borderRight: i < 6 ? '1px solid #EAECF0' : 'none',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              {bucket?.milestones.map(m => <MilestoneChip key={m.dagrun_pm_milestoneid} milestone={m} />)}
              {bucket?.tasks.map(t => <TaskChip key={t.dagrun_pm_taskid} task={t} onClick={() => onTaskClick(t)} />)}
              {!bucket && <div style={{ fontSize: 11, color: '#B8BEC9', fontFamily: 'var(--font-ui)', fontStyle: 'italic' }}>—</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Chips ─────────────────────────────────────────────────────────────
const PRIORITY_COLOR: Record<number, string> = {
  100000003: '#DC2626', // Critical
  100000002: '#EA580C', // High
  100000001: '#D97706', // Medium
  100000000: '#5A6272', // Low
};
function TaskChip({ task, onClick }: { task: Task; onClick: () => void }) {
  const isDone = task.dagrun_pm_status === 100000003;
  const accent = PRIORITY_COLOR[task.dagrun_pm_priority] ?? 'var(--brand)';
  return (
    <button
      type="button"
      onClick={onClick}
      title={task.dagrun_pm_name}
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '2px 6px',
        background: isDone ? 'var(--surface-2)' : '#fff',
        border: `1px solid ${isDone ? 'var(--border)' : accent + '33'}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 3,
        cursor: 'pointer',
        width: '100%',
        overflow: 'hidden',
        textAlign: 'left',
        fontFamily: 'var(--font-ui)',
        fontSize: 11,
        color: isDone ? 'var(--ink-4)' : 'var(--ink)',
        textDecoration: isDone ? 'line-through' : 'none',
        whiteSpace: 'nowrap', textOverflow: 'ellipsis',
      }}
    >{task.dagrun_pm_name}</button>
  );
}
function MilestoneChip({ milestone }: { milestone: Milestone }) {
  const complete = milestone.dagrun_pm_status === 100000002;
  return (
    <div
      title={milestone.dagrun_pm_name}
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '2px 6px',
        background: complete ? 'var(--brand)' : 'var(--brand-mist)',
        color: complete ? '#fff' : 'var(--brand)',
        borderRadius: 3,
        fontFamily: 'var(--font-ui)',
        fontSize: 11, fontWeight: 600,
        whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden',
      }}
    >
      <span style={{ fontSize: 10 }}>◆</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{milestone.dagrun_pm_name}</span>
    </div>
  );
}
